import { useState, useRef, useEffect } from 'react';
import * as GaussianSplats3D from '@mkkellogg/gaussian-splats-3d';
import * as THREE from 'three';
import { useLocation } from 'react-router-dom';
import { models_input_example } from '../../inputs_example';

function create_normal_viewer(path, renderer, options) {
  const { cameraUp, initialCameraPosition, initialCameraLookAt } = options;
  const viewer = new GaussianSplats3D.Viewer({
    renderer,
    gpuAcceleratedSort: false,
    sharedMemoryForWorkers: false,
    cameraUp: cameraUp,
    initialCameraPosition: initialCameraPosition,
    initialCameraLookAt: initialCameraLookAt,
  });
  const splat_promise = viewer.addSplatScene(path);

  return [viewer, splat_promise];
}

function create_ar_viewer(path, renderer, options) {
  const { rotation, position } = options;
  const { vFrom, vTo } = rotation;
  const viewer = new GaussianSplats3D.Viewer({
    gpuAcceleratedSort: false,
    sharedMemoryForWorkers: false,
    renderer,
    webXRMode: GaussianSplats3D.WebXRMode.AR,
  });
  const splat_promise = viewer.addSplatScene(path, {
    rotation: new THREE.Quaternion()
      .setFromUnitVectors(
        new THREE.Vector3(...vFrom).normalize(),
        new THREE.Vector3(...vTo)
      )
      .toArray(),
    position,
  });

  return [viewer, splat_promise];
}

function create_vr_viewer(path, renderer, options) {
  const { rotation, position } = options;
  const { vFrom, vTo } = rotation;
  const viewer = new GaussianSplats3D.Viewer({
    gpuAcceleratedSort: false,
    sharedMemoryForWorkers: false,
    renderer,
    webXRMode: GaussianSplats3D.WebXRMode.VR,
  });
  const splat_promise = viewer.addSplatScene(path, {
    rotation: new THREE.Quaternion()
      .setFromUnitVectors(
        new THREE.Vector3(...vFrom).normalize(),
        new THREE.Vector3(...vTo)
      )
      .toArray(),
    position,
  });

  return [viewer, splat_promise];
}

function viewer_factory(name, type) {
  const renderer = new THREE.WebGLRenderer();
  const renderWidth = window.innerWidth;
  const renderHeight = window.innerHeight;
  renderer.setSize(renderWidth, renderHeight);

  const model_info = models_input_example[name];
  const url = model_info.path_url;

  switch (type) {
    case 'normal':
      return [
        renderer,
        ...create_normal_viewer(
          url,
          renderer,
          model_info.normal.viewer_options
        ),
      ];
    case 'ar':
      return [
        renderer,
        ...create_ar_viewer(url, renderer, model_info.ar.splat_scene_options),
      ];
    case 'vr':
      return [
        renderer,
        ...create_vr_viewer(url, renderer, model_info.vr.splat_scene_options),
      ];
    default:
      return null;
  }
}

function ThreeDModel() {
  const location = useLocation(); // Hook to access location object
  const query = new URLSearchParams(location.search);
  const urlType = query.get('type'); // Get 'type' parameter from URL

  const [type, setType] = useState(urlType || 'normal'); // Set default type based on URL parameter
  const [model_name, setModelName] = useState('');

  const canvas_root_element_ref = useRef(null);
  const [canvas_, setCanvas] = useState({
    renderer: null,
    viewer: null,
  });

  const init = () => {
    if (canvas_.renderer) {
      remove_canvas();
    }
    const [renderer, viewer, viewer_promise] = viewer_factory(model_name, type);
    canvas_root_element_ref.current.appendChild(renderer.domElement);
    viewer_promise.then(() => {
      viewer.start();
    });
    setCanvas({ renderer, viewer });

    const onResize = () => renderer.setSize(window.innerWidth, window.innerHeight);
    window.addEventListener('resize', onResize);

    return () => {
      viewer.stop();
      window.removeEventListener('resize', onResize);
    };
  };

  const remove_canvas = () => {
    if (canvas_.viewer) {
      canvas_.viewer.dispose();
    }
    if (canvas_.renderer && canvas_.renderer.domElement && canvas_.renderer.domElement.parentNode) {
      canvas_.renderer.domElement.parentNode.removeChild(canvas_.renderer.domElement);
    }
    setCanvas({ renderer: null, viewer: null });
  };

  useEffect(() => {
    const firstModelKey = Object.keys(models_input_example)[0];
    if (firstModelKey) {
      setModelName(firstModelKey);
    }
  }, []);

  useEffect(() => {
    if (model_name) {
      const cleanup = init();
      return () => {
        cleanup();
        remove_canvas();
      };
    }
  }, [model_name, type]);

  useEffect(() => {
    return () => {
      remove_canvas();
    };
  }, []);

  return (
    <div ref={canvas_root_element_ref} style={{ width: '100vw', height: '100vh' }}></div>
  );
}

export default ThreeDModel;
